// src/services/servicesCatalogService.js
//
// Frontend API wrapper for concrete Services in the Catalog domain.
//
// PURPOSE
// -------
// This service handles concrete service rows maintained by employees.
//
// Examples:
// - Hydra Facial Premium
// - Brow Lamination
// - Relax Massage 60 min
//
// CURRENT TARGET MODEL
// --------------------
// Service = one concrete sellable service item.
//
// The target service model is intentionally cleaned up:
// - no name_internal
// - legacy price is replaced by VAT-aware pricing fields
// - service images/media are supported for future website publication
//
// Important current fields:
// - section_id
// - name_public
// - description_short
// - duration_minutes
// - price_label
// - price_entry_mode
// - price_input
// - vat_rate_percent
// - price_net
// - price_vat
// - price_gross
// - sort_order
// - is_active
// - publish_to_website
//
// ARCHITECTURAL NOTE
// ------------------
// This service wrapper belongs to the NEW structured catalog area.
// It does not force existing operational modules such as visits/vouchers to
// immediately reference services by FK. Those modules may still keep service
// names as text until a controlled migration is planned.
//
// IMAGE / MEDIA NOTE
// ------------------
// Images may be returned by backend as:
//   images: [
//     {
//       id,
//       owner_type / service_id,
//       file_name,
//       original_name,
//       mime_type,
//       storage_path,
//       file_url
//     }
//   ]
//
// The frontend should use file_url or fetchServiceImageBlob(...), not
// storage_path. storage_path is internal backend metadata and is not a browser
// URL.
//
// SECURITY NOTE
// -------------
// Product/service image endpoints are usually protected by the same cookie/session
// authentication as the rest of hb_visits. For that reason, simple <img src>
// may work only if the browser sends cookies correctly for that request.
// fetchServiceImageBlob(...) is the safer pattern because it uses apiClient.
//
// Logging policy and error handling are aligned with existing hb_visits service
// wrappers such as visitService.js and productsService.js.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

/**
 * Price entry modes supported by backend.
 *
 * GROSS_INCLUDES_VAT:
 * - user enters the final customer price
 * - VAT is calculated inside that amount
 *
 * NET_PLUS_VAT:
 * - user enters net value
 * - VAT is added on top
 */
export const SERVICE_PRICE_ENTRY_MODES = {
  GROSS_INCLUDES_VAT: "GROSS_INCLUDES_VAT",
  NET_PLUS_VAT: "NET_PLUS_VAT",
};

/* -------------------------------------------------------------------------- */
/* Error helpers                                                              */
/* -------------------------------------------------------------------------- */

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;
  e.code = err?.response?.data?.code || err?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ServicesCatalogService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "SERVICE_ID_MISSING";
    throw e;
  }
}

function isBlank(v) {
  return v === undefined || v === null || `${v}`.trim() === "";
}

/**
 * Validate payload on frontend before calling backend.
 *
 * IMPORTANT
 * ---------
 * This is only user-friendly early validation.
 * Backend remains authoritative.
 */
function assertServicePayload(context, payload) {
  if (!payload || typeof payload !== "object") {
    const e = new Error(`${context}: payload object is required.`);
    e.code = "SERVICE_PAYLOAD_MISSING";
    throw e;
  }

  if (isBlank(payload.section_id)) {
    const e = new Error(`${context}: section_id is required.`);
    e.code = "SERVICE_SECTION_ID_MISSING";
    throw e;
  }

  if (isBlank(payload.name_public)) {
    const e = new Error(`${context}: name_public is required.`);
    e.code = "SERVICE_NAME_PUBLIC_MISSING";
    throw e;
  }

  if (
    payload.price_entry_mode &&
    !Object.values(SERVICE_PRICE_ENTRY_MODES).includes(payload.price_entry_mode)
  ) {
    const e = new Error(`${context}: price_entry_mode is invalid.`);
    e.code = "SERVICE_PRICE_ENTRY_MODE_INVALID";
    throw e;
  }
}

/* -------------------------------------------------------------------------- */
/* Image URL / blob helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Build a browser-usable service image URL.
 *
 * Preferred source:
 * - image.file_url returned by backend
 *
 * Fallback:
 * - derive URL from service/image IDs
 *
 * IMPORTANT
 * ---------
 * This function returns an application URL, not a filesystem path.
 */
export function buildServiceImageUrl(image, serviceIdFallback = null) {
  if (!image) return "";

  if (image.file_url) {
    return image.file_url;
  }

  const imageId = image.id;
  const serviceId =
    serviceIdFallback ||
    image.service_id ||
    image.owner_id ||
    image.entity_id ||
    image.serviceId;

  if (!serviceId || !imageId) return "";

  return `/catalog/services/${serviceId}/images/${imageId}/file`;
}

/**
 * Fetch a protected service image as Blob.
 *
 * Why this exists:
 * - hb_visits API endpoints are cookie/session protected
 * - apiClient already owns credentials, auth refresh and common headers
 * - object URLs created from blobs work reliably in <img src={...}>
 *
 * Usage:
 *   const blob = await fetchServiceImageBlob(image)
 *   const objectUrl = URL.createObjectURL(blob)
 */
export const fetchServiceImageBlob = async (image, serviceIdFallback = null) => {
  try {
    const url = buildServiceImageUrl(image, serviceIdFallback);

    if (!url) {
      const e = new Error("fetchServiceImageBlob: image URL is missing.");
      e.code = "SERVICE_IMAGE_URL_MISSING";
      throw e;
    }

    const res = await apiClient.get(url, {
      withCredentials: true,
      responseType: "blob",
    });

    return res?.data;
  } catch (err) {
    throwApiError("fetchServiceImageBlob", err);
  }
};

/* -------------------------------------------------------------------------- */
/* Read operations                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Fetch catalog services.
 *
 * Backend:
 *   GET /catalog/services
 *
 * Optional query params:
 *   - active=true|false
 *   - section_id=<id>
 *   - main_category_id=<id>       optional if backend supports it
 *   - publish_to_website=true|false
 *   - q=<search text>
 *
 * Return:
 * - expected: array of service objects
 * - defensive fallback: [] if backend payload is missing / malformed
 */
export const fetchCatalogServices = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/services", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchCatalogServices", err);
  }
};

/**
 * Fetch one service by ID.
 *
 * Backend:
 *   GET /catalog/services/:id
 *
 * Used by:
 * - ServicesPage.jsx in edit mode
 * - reload after save
 * - reload after image upload/delete/set-primary
 *
 * Expected backend response:
 * - one service object
 * - ideally with section and images included
 */
export const fetchCatalogServiceById = async (id) => {
  try {
    assertId("fetchCatalogServiceById", id);

    const res = await apiClient.get(`/catalog/services/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? null;
  } catch (err) {
    throwApiError("fetchCatalogServiceById", err);
  }
};

/* -------------------------------------------------------------------------- */
/* Create / update operations                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Create a new catalog service.
 *
 * Backend:
 *   POST /catalog/services
 *
 * Target body:
 *   {
 *     section_id: number,
 *     name_public: string,
 *     description_short?: string,
 *     duration_minutes?: number,
 *     price_label?: string,
 *     price_entry_mode?: "GROSS_INCLUDES_VAT" | "NET_PLUS_VAT",
 *     price_input?: number|string|null,
 *     vat_rate_percent?: number|string,
 *     sort_order?: number,
 *     is_active?: boolean,
 *     publish_to_website?: boolean
 *   }
 *
 * Backend should calculate:
 * - price_net
 * - price_vat
 * - price_gross
 */
export const createCatalogService = async (payload) => {
  try {
    assertServicePayload("createCatalogService", payload);

    const res = await apiClient.post("/catalog/services", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createCatalogService", err);
  }
};

/**
 * Update an existing catalog service.
 *
 * Backend:
 *   PUT /catalog/services/:id
 */
export const updateCatalogService = async (id, payload) => {
  try {
    assertId("updateCatalogService", id);
    assertServicePayload("updateCatalogService", payload);

    const res = await apiClient.put(`/catalog/services/${id}`, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("updateCatalogService", err);
  }
};

/**
 * Toggle active state of a catalog service.
 *
 * Backend:
 *   PATCH /catalog/services/:id/active
 *
 * Body:
 *   { active: true|false }
 *
 * This is useful for quick list-level actions without opening the full editor.
 */
export const toggleCatalogServiceActive = async (id, active) => {
  try {
    assertId("toggleCatalogServiceActive", id);

    const res = await apiClient.patch(
      `/catalog/services/${id}/active`,
      { active: !!active },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("toggleCatalogServiceActive", err);
  }
};

/**
 * Delete a concrete catalog service.
 *
 * Backend:
 *   DELETE /catalog/services/:id
 *
 * Backend may reject delete when:
 * - the service already has publication history
 * - the service is already used by future operational modules
 */
export const deleteCatalogService = async (id) => {
  try {
    assertId("deleteCatalogService", id);

    const res = await apiClient.delete(`/catalog/services/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteCatalogService", err);
  }
};

/* -------------------------------------------------------------------------- */
/* Service images / media                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Upload one or more service images.
 *
 * Backend:
 *   POST /catalog/services/:id/images
 *
 * Request:
 *   multipart/form-data
 *   files field name: images
 *
 * Browser security note:
 * - A web app cannot read arbitrary local file paths from the user's machine.
 * - "Selecting a path" is implemented through the standard file picker.
 * - Drag and drop provides File objects as well.
 */
export const uploadServiceImages = async (serviceId, files) => {
  try {
    assertId("uploadServiceImages", serviceId);

    const fileList = Array.isArray(files) ? files : Array.from(files || []);

    if (fileList.length === 0) {
      const e = new Error("uploadServiceImages: at least one file is required.");
      e.code = "SERVICE_IMAGE_FILES_MISSING";
      throw e;
    }

    const formData = new FormData();

    fileList.forEach((file) => {
      formData.append("images", file);
    });

    const res = await apiClient.post(
      `/catalog/services/${serviceId}/images`,
      formData,
      {
        withCredentials: true,

        // Important:
        // Do not manually set multipart boundary.
        // Axios/browser will set Content-Type with boundary automatically.
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("uploadServiceImages", err);
  }
};

/**
 * Set one service image as primary.
 *
 * Backend:
 *   PATCH /catalog/services/:id/images/:imageId/primary
 *
 * Used by ServicesPage.jsx gallery.
 */
export const setServiceImagePrimary = async (serviceId, imageId) => {
  try {
    assertId("setServiceImagePrimary serviceId", serviceId);
    assertId("setServiceImagePrimary imageId", imageId);

    const res = await apiClient.patch(
      `/catalog/services/${serviceId}/images/${imageId}/primary`,
      {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("setServiceImagePrimary", err);
  }
};

/**
 * Delete one service image.
 *
 * Backend:
 *   DELETE /catalog/services/:id/images/:imageId
 */
export const deleteServiceImage = async (serviceId, imageId) => {
  try {
    assertId("deleteServiceImage serviceId", serviceId);
    assertId("deleteServiceImage imageId", imageId);

    const res = await apiClient.delete(
      `/catalog/services/${serviceId}/images/${imageId}`,
      {
        withCredentials: true,
      }
    );

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteServiceImage", err);
  }
};

/* -------------------------------------------------------------------------- */
/* Backward-compatible aliases inside frontend only                           */
/* -------------------------------------------------------------------------- */

/**
 * Alias names matching the ProductPage naming style.
 *
 * These aliases are useful if ServicesPage.jsx was cloned from ProductPage.jsx
 * and you want readable imports with "CatalogService" prefix.
 */
export const uploadCatalogServiceImages = uploadServiceImages;
export const setCatalogServiceImagePrimary = setServiceImagePrimary;
export const deleteCatalogServiceImage = deleteServiceImage;
export const fetchCatalogServiceImageBlob = fetchServiceImageBlob;
export const buildCatalogServiceImageUrl = buildServiceImageUrl;

/* -------------------------------------------------------------------------- */
/* Default export                                                             */
/* -------------------------------------------------------------------------- */

export default {
  SERVICE_PRICE_ENTRY_MODES,

  fetchCatalogServices,
  fetchCatalogServiceById,

  createCatalogService,
  updateCatalogService,
  toggleCatalogServiceActive,
  deleteCatalogService,

  buildServiceImageUrl,
  fetchServiceImageBlob,
  uploadServiceImages,
  setServiceImagePrimary,
  deleteServiceImage,

  uploadCatalogServiceImages,
  setCatalogServiceImagePrimary,
  deleteCatalogServiceImage,
  fetchCatalogServiceImageBlob,
  buildCatalogServiceImageUrl,
};